import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { KeyRound, Building2, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useInvitationByToken, useAcceptInvitation } from "@/hooks/useInvitations";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const { data: invitation, isLoading: invLoading, error: invError } = useInvitationByToken(token);
  const acceptMutation = useAcceptInvitation();

  const isExpired = invitation && new Date(invitation.expires_at) < new Date();
  const isAlreadyAccepted = invitation?.accepted;

  const handleAccept = async () => {
    if (!user || !token) return;
    setAccepting(true);
    try {
      await acceptMutation.mutateAsync({ token, userId: user.id });
      setAccepted(true);
      toast({
        title: "Invitation accepted! 🎉",
        description: `Welcome to ${invitation?.organization?.name ?? "Stratakey"}. Redirecting to your dashboard…`,
      });
      // Reload to refresh assignments in auth context, then go to dashboard
      setTimeout(() => {
        window.location.href = "/dashboard";
      }, 2000);
    } catch (err: any) {
      toast({ title: "Failed to accept", description: err.message, variant: "destructive" });
    } finally {
      setAccepting(false);
    }
  };

  const handleSignUp = () => {
    // Navigate to sign-up with token preserved in URL so it can be accepted after account creation
    navigate(`/signup?invite=${token}`);
  };

  if (authLoading || invLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Header */}
        <div className="flex flex-col items-center gap-2">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary">
            <KeyRound className="h-6 w-6 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">You've Been Invited</h1>
          <p className="text-sm text-muted-foreground">Stratakey STR Compliance Platform</p>
        </div>

        {/* Error States */}
        {(invError || !invitation) && (
          <Card>
            <CardContent className="flex flex-col items-center gap-4 py-10">
              <AlertCircle className="h-12 w-12 text-destructive" />
              <h2 className="text-lg font-bold">Invalid Invitation</h2>
              <p className="text-center text-sm text-muted-foreground">
                This invitation link is invalid or does not exist. Please request a new invitation from your strata council.
              </p>
              <Button variant="outline" onClick={() => navigate("/login")}>Go to Login</Button>
            </CardContent>
          </Card>
        )}

        {invitation && isExpired && (
          <Card>
            <CardContent className="flex flex-col items-center gap-4 py-10">
              <AlertCircle className="h-12 w-12 text-amber-500" />
              <h2 className="text-lg font-bold">Invitation Expired</h2>
              <p className="text-center text-sm text-muted-foreground">
                This invitation expired on {new Date(invitation.expires_at).toLocaleDateString()}. Please request a new one.
              </p>
              <Button variant="outline" onClick={() => navigate("/login")}>Go to Login</Button>
            </CardContent>
          </Card>
        )}

        {invitation && isAlreadyAccepted && (
          <Card>
            <CardContent className="flex flex-col items-center gap-4 py-10">
              <CheckCircle2 className="h-12 w-12 text-green-500" />
              <h2 className="text-lg font-bold">Already Accepted</h2>
              <p className="text-center text-sm text-muted-foreground">This invitation has already been accepted.</p>
              <Button onClick={() => navigate("/dashboard")}>Go to Dashboard</Button>
            </CardContent>
          </Card>
        )}

        {/* Valid invitation */}
        {invitation && !isExpired && !isAlreadyAccepted && !accepted && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                {invitation.organization?.name ?? "A Strata Organization"}
              </CardTitle>
              <CardDescription>
                You've been invited to join as a <strong className="capitalize">{invitation.role.replace("_", " ")}</strong>.
                {invitation.role === "host" && invitation.unit && (
                  <> You have been assigned to <strong>Unit {(invitation.unit as any).unit_number}</strong>.</>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg bg-muted p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Role</span>
                  <span className="font-medium capitalize">{invitation.role.replace("_", " ")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Organization</span>
                  <span className="font-medium">{invitation.organization?.name ?? "—"}</span>
                </div>
                {invitation.role === "host" && invitation.unit && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Unit</span>
                    <span className="font-medium">Unit {(invitation.unit as any).unit_number}</span>
                  </div>
                )}
                {invitation.role === "host" && invitation.night_limit && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Night Limit</span>
                    <span className="font-medium">{invitation.night_limit} nights / year</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Expires</span>
                  <span className="font-medium">{new Date(invitation.expires_at).toLocaleDateString()}</span>
                </div>
              </div>

              {isAuthenticated ? (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    You're signed in as <strong>{user?.email}</strong>. Click below to accept the invitation.
                  </p>
                  {invitation.role === "host" && (
                    <p className="text-xs text-muted-foreground bg-blue-50 border border-blue-200 rounded-md p-2">
                      ℹ️ Accepting this invitation will create your host profile for{" "}
                      {invitation.unit ? `Unit ${(invitation.unit as any).unit_number}` : "your assigned unit"}.
                      Your registration will be in <strong>pending</strong> status until approved by the property manager.
                    </p>
                  )}
                  <Button className="w-full" onClick={handleAccept} disabled={accepting}>
                    {accepting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Accepting...</> : "Accept Invitation"}
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    To accept this invitation, please sign in or create a new account.
                  </p>
                  <Button className="w-full" onClick={() => navigate(`/login?redirect=/accept-invite?token=${token}`)}>
                    Sign In to Accept
                  </Button>
                  <Button variant="outline" className="w-full" onClick={handleSignUp}>
                    Create Account & Accept
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Success state */}
        {accepted && (
          <Card>
            <CardContent className="flex flex-col items-center gap-4 py-10">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold">Invitation Accepted!</h2>
              <p className="text-center text-sm text-muted-foreground">
                You now have access to <strong>{invitation?.organization?.name}</strong>. Redirecting to your dashboard...
              </p>
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
